import React, { useState } from 'react';

import { Grid, Loader, Message } from 'semantic-ui-react';

import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _map from 'lodash/map';
import { useDispatch, useSelector } from 'react-redux';

import { flagBusinessSaved } from 'actions/business';
import MediaMultipleImagesSelector from 'components/modules/business-profile/components/MediaMultipleImagesSelector';
import MediaSingleImageSelector from 'components/modules/business-profile/components/MediaSingleImageSelector';
import {
  MEDIA_CATEGORY_COVER,
  MEDIA_CATEGORY_PROFILE,
  SINGLE_IMAGE_CATEGORIES,
} from 'components/modules/business-profile/constants/media';
import { MediaContextProvider } from 'components/modules/business-profile/contexts/Media.context';
import { getGoogleLocationProviderSettings } from 'components/modules/business-profile/services/provider-service';
import { useBusinessFiles } from 'components/modules/files/hooks';
import useFilesProviderMetadata from 'components/modules/files/hooks/use-business-files-provider-metadata/useBusinessFilesProviderMetadata';
import useAsyncEffect from 'components/modules/foundation/hooks/use-async-effect';
import useProviderServiceSettings from 'components/modules/integrations/hooks/use-provider-service-settings';
import LocationSelect from 'components/pages/ecommerce/common/LocationSelect';
import API from 'libs/api';
import { multiCaseGet } from 'libs/format';
import { selectBusinessId, selectBusinessLocations, selectBusinessType } from 'selectors';

import {
  TAB_DESCRIPTION,
  NO_SETTINGS_WARNING_MESSAGE,
  MEDIA_PUSH_NOT_ENABLED_WARNING_MESSAGE,
} from './MediaTab.constants';
import {
  createFileProviderMetadataPayload,
  filterOutByAssociations,
  getFileByAssociations,
  getFileProviderMetadata,
  keepByAssociations,
} from './MediaTab.utils';
import { filterGoogleConfiguredLocations } from '../BusinessInfoTab/BusinessInfoTab.utils';

import './MediaTab.scss';

const propTypes = {};

const defaultProps = {};

export default function MediaTab() {
  const dispatch = useDispatch();

  const businessId = useSelector(selectBusinessId);
  const businessType = useSelector(selectBusinessType);

  const { providerServiceSettings, loading: loadingProviderServiceSettings } =
    useProviderServiceSettings();

  const locations = useSelector(selectBusinessLocations);
  const [selectedLocationId, setSelectedLocationId] = useState(_get(locations, '0.id', null));
  const [allGmbLocations, setAllGmbLocations] = useState([]);

  const locationSettings = getGoogleLocationProviderSettings(providerServiceSettings);
  const locationSetting = locationSettings.find(
    (current) => _get(current, 'serviceData.fishermanLocationId') === selectedLocationId,
  );
  const locationSettingId = _get(locationSetting, 'id', null);
  const gmbAccountId = _get(locationSetting, 'serviceData.gmbAccountId', null);
  const gmbLocationId = _get(locationSetting, 'serviceData.gmbLocationId', null);
  const gmbLocation = allGmbLocations.find(
    (location) => multiCaseGet(location, 'locationId') === gmbLocationId,
  );
  const locationSettingServiceData = _get(locationSetting, 'serviceData', {});
  const fishermanLocationId = _get(locationSettingServiceData, 'fishermanLocationId');
  const mediaPushEnabled = _get(locationSettingServiceData, 'settings.media.push.settings.enabled');
  const mediaPushSupported = _get(locationSettingServiceData, 'settings.media.push.supported');
  const isPushingEnabled = mediaPushEnabled && mediaPushSupported;

  const [loading, setLoading] = useState(true);

  const {
    filesProviderMetadata: allFilesMetadata,
    loading: loadingFilesProviderMetadata,
    refresh: refreshFilesMetadata,
    bulkUpdateFileProviderMetadata,
  } = useFilesProviderMetadata();

  const filesMetadata = allFilesMetadata.filter(
    (fileMetadata) =>
      _get(fileMetadata, 'location') === fishermanLocationId &&
      _get(fileMetadata, 'providerSetting') === locationSettingId,
  );

  const selectedBusinessFileIds = _map(filesMetadata, 'businessFile');

  const { businessFiles: selectedFiles, loading: loadingFiles } = useBusinessFiles({
    fileIds: selectedBusinessFileIds,
    forceInitialFetch: true,
  });

  function filterLocations(location) {
    return filterGoogleConfiguredLocations(location, locationSettings);
  }

  useAsyncEffect(async () => {
    try {
      const allAccountLocations = await API.getGoogleMyBusinessLocations(
        businessType,
        businessId,
        gmbAccountId,
      );

      setAllGmbLocations(allAccountLocations);
    } finally {
      setLoading(false);
    }
  }, []);

  async function onSelectSingleImage(association, newFile) {
    const newFileId = _get(newFile, 'id', null);
    const previousFilesMetadata = getFileByAssociations(selectedFiles, filesMetadata, [
      association,
    ]);

    if (previousFilesMetadata && _get(previousFilesMetadata, 'businessFile') === newFileId) {
      // do nothing if the same file is selected
      return;
    }

    const newFilesMetadata = filterOutByAssociations(filesMetadata, [association]);

    if (newFileId) {
      newFilesMetadata.push(
        createFileProviderMetadataPayload(fishermanLocationId, locationSettingId, newFile, {
          association,
        }),
      );
    }

    await bulkUpdateFileProviderMetadata(newFilesMetadata, {
      setId: locationSettingId,
      locId: fishermanLocationId,
    });
    dispatch(flagBusinessSaved(true));
  }

  function onChangeLocation(newLocation) {
    const newLocationId = _get(newLocation, 'id');
    setSelectedLocationId(newLocationId);
  }

  async function onSelectMultipleImages(newFiles) {
    const newFilesMetadata = keepByAssociations(filesMetadata, SINGLE_IMAGE_CATEGORIES);

    newFiles.forEach((newFile) => {
      const newFileId = _get(newFile, 'id', null);

      const fileProviderMetadata = getFileProviderMetadata(newFileId, filesMetadata);

      if (_isEmpty(fileProviderMetadata)) {
        newFilesMetadata.push(
          createFileProviderMetadataPayload(fishermanLocationId, locationSettingId, newFile),
        );
      } else {
        // reuse existing file metadata as file was not deselected
        newFilesMetadata.push(fileProviderMetadata);
      }
    });

    await bulkUpdateFileProviderMetadata(newFilesMetadata, {
      setId: locationSettingId,
      locId: fishermanLocationId,
    });
    dispatch(flagBusinessSaved(true));
  }

  const finalLoading =
    loading || loadingFiles || loadingFilesProviderMetadata || loadingProviderServiceSettings;

  const contextValue = {
    filesMetadata,
    selectedFiles,
    refreshFilesMetadata,
    onSelectSingleImage,
    onSelectMultipleImages,
    gmbLocation,
  };

  return (
    <MediaContextProvider value={contextValue}>
      <div className="media-tab">
        {finalLoading && <Loader active inline />}
        {!finalLoading && (
          <>
            <Message className="tab-description" content={TAB_DESCRIPTION} />
            <div className="controls">
              <LocationSelect
                activeLocation={selectedLocationId}
                onChangeLocation={onChangeLocation}
                global={false}
                fluid
                hideIfSingleLocation
                hideAllLocationsOption
                filterLocations={filterLocations}
              />
            </div>

            {_isEmpty(locationSetting) && (
              <Message
                warning
                className="no-settings-for-location"
                content={NO_SETTINGS_WARNING_MESSAGE}
              />
            )}

            {!_isEmpty(locationSetting) && !isPushingEnabled && (
              <Message
                warning
                className="media-push-not-enabled"
                content={MEDIA_PUSH_NOT_ENABLED_WARNING_MESSAGE}
              />
            )}

            {!_isEmpty(locationSetting) && isPushingEnabled && (
              <>
                <div className="profile-photos">
                  <Grid stackable doubling>
                    <Grid.Column width={6}>
                      <MediaSingleImageSelector association={MEDIA_CATEGORY_PROFILE} />
                    </Grid.Column>
                    <Grid.Column width={6}>
                      <MediaSingleImageSelector association={MEDIA_CATEGORY_COVER} />
                    </Grid.Column>
                    <Grid.Column width={4} />
                  </Grid>
                </div>
                <div className="additional-photos">
                  <MediaMultipleImagesSelector />
                </div>
              </>
            )}
          </>
        )}
      </div>
    </MediaContextProvider>
  );
}

MediaTab.propTypes = propTypes;
MediaTab.defaultProps = defaultProps;
